/**
 * The Fuel service catalog (docs/rebuild/fuel.md section 7), shared by both
 * APIs, the seed and the group editor. Codes are `<ENTITY>_<ACTION>`, grouped
 * by a parent for display, as in POS.
 *
 * `app` says which application a service belongs to. A user is active in the
 * FUEL_OPERATOR platform when the group holds any OPERATOR service and in
 * FUEL_DASHBOARD when it holds any DASHBOARD service and in FUEL_PDA when it
 * holds any PDA service (the PDA_CUSTOMER_* services belong to the dashboard);
 * BOTH (product access) never activates a platform by itself. Operator services use their own
 * entities so the dependency rule never pulls dashboard lists into an
 * operator-only group.
 */
export const FUEL_APPS = ['DASHBOARD', 'OPERATOR', 'PDA'];
export const SERVICE_PARENTS = [
    { code: 'OPERATIONS', nameAr: 'العمليات', nameEn: 'Operations' },
    { code: 'PRODUCTS', nameAr: 'المنتجات', nameEn: 'Products' },
    { code: 'DASHBOARD', nameAr: 'لمحة عامة', nameEn: 'Overview' },
    { code: 'REVOLVING_BALANCES', nameAr: 'الأرصدة المدورة', nameEn: 'Carried balances' },
    { code: 'PURCHASES', nameAr: 'المشتريات', nameEn: 'Purchases' },
    { code: 'SALES', nameAr: 'المبيعات', nameEn: 'Sales' },
    { code: 'DEBT_MOVEMENTS', nameAr: 'حركات الذمم', nameEn: 'Debt movements' },
    { code: 'PAYMENTS', nameAr: 'المدفوعات', nameEn: 'Payments' },
    { code: 'RECEIPTS', nameAr: 'المقبوضات', nameEn: 'Receipts' },
    { code: 'MOVEMENTS', nameAr: 'الحركات', nameEn: 'Movements' },
    { code: 'DEBTORS', nameAr: 'عملاء الذمم', nameEn: 'Debtors' },
    { code: 'MESSAGES', nameAr: 'الرسائل', nameEn: 'Messages' },
    { code: 'DELIVERY', nameAr: 'التسليم', nameEn: 'Delivery' },
    { code: 'YEARS', nameAr: 'إدارة السنوات', nameEn: 'Year management' },
    { code: 'CLOSES', nameAr: 'حركات الإغلاق', nameEn: 'Shift closes' },
    { code: 'INVENTORY_COUNT', nameAr: 'وضع الجرد', nameEn: 'Inventory count mode' },
    { code: 'TRASH', nameAr: 'سلة المحذوفات', nameEn: 'Trash' },
    { code: 'CATEGORIES', nameAr: 'التصنيفات', nameEn: 'Categories' },
    { code: 'SETTINGS', nameAr: 'الأسعار والإعدادات', nameEn: 'Prices and settings' },
    { code: 'USERS', nameAr: 'المستخدمين', nameEn: 'Users' },
    { code: 'GROUPS', nameAr: 'المجموعات', nameEn: 'Groups' },
    { code: 'PDA', nameAr: 'الـ PDA', nameEn: 'PDA' },
    { code: 'PDA_CUSTOMERS', nameAr: 'عملاء المشتريات', nameEn: 'Purchase customers' },
    { code: 'NATIONALITIES', nameAr: 'الجنسيات', nameEn: 'Nationalities' },
];
export const SERVICE_CATALOG = [
    {
        code: 'OPERATOR_REVOLVING_BALANCE_CREATE',
        entity: 'OPERATOR_REVOLVING_BALANCE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال الرصيد المدور',
        nameEn: 'Enter carried balance',
    },
    {
        code: 'OPERATOR_PURCHASE_CREATE',
        entity: 'OPERATOR_PURCHASE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال الشراء',
        nameEn: 'Enter purchase',
    },
    {
        code: 'OPERATOR_SALE_CREATE',
        entity: 'OPERATOR_SALE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال البيع النقدي',
        nameEn: 'Enter cash sale',
    },
    {
        code: 'OPERATOR_DEBT_SALE_CREATE',
        entity: 'OPERATOR_DEBT_SALE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال بيع الذمم',
        nameEn: 'Enter debt sale',
    },
    {
        code: 'OPERATOR_DEBT_REPAYMENT_CREATE',
        entity: 'OPERATOR_DEBT_REPAYMENT',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال سداد الذمم',
        nameEn: 'Enter debt repayment',
    },
    {
        code: 'OPERATOR_PAYMENT_CREATE',
        entity: 'OPERATOR_PAYMENT',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال المدفوعات',
        nameEn: 'Enter payment',
    },
    {
        code: 'OPERATOR_RECEIPT_CREATE',
        entity: 'OPERATOR_RECEIPT',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إدخال المقبوضات',
        nameEn: 'Enter receipt',
    },
    {
        code: 'OPERATOR_SHIFT_CLOSE',
        entity: 'OPERATOR_SHIFT',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إغلاق الشفت',
        nameEn: 'Close the shift',
    },
    {
        code: 'OPERATOR_PURCHASE_PRICE_COMPLETE',
        entity: 'OPERATOR_PURCHASE_PRICE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إكمال سعر الشراء',
        nameEn: 'Complete purchase price',
    },
    {
        code: 'OPERATOR_SALE_PRICE_COMPLETE',
        entity: 'OPERATOR_SALE_PRICE',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'إكمال سعر البيع',
        nameEn: 'Complete sale price',
    },
    {
        code: 'OPERATOR_TRANSACTION_DELIVER',
        entity: 'OPERATOR_TRANSACTION',
        parentCode: 'OPERATIONS',
        app: 'OPERATOR',
        nameAr: 'تأكيد تسليم الحركة',
        nameEn: 'Confirm transaction delivery',
    },
    {
        code: 'DIESEL_ACCESS',
        entity: 'DIESEL',
        parentCode: 'PRODUCTS',
        app: 'BOTH',
        nameAr: 'المحروقات',
        nameEn: 'Diesel',
    },
    {
        code: 'PETROL_ACCESS',
        entity: 'PETROL',
        parentCode: 'PRODUCTS',
        app: 'BOTH',
        nameAr: 'البنزين',
        nameEn: 'Petrol',
    },
    {
        code: 'DASHBOARD_VIEW',
        entity: 'DASHBOARD',
        parentCode: 'DASHBOARD',
        app: 'DASHBOARD',
        nameAr: 'عرض لمحة عامة',
        nameEn: 'View overview',
    },
    {
        code: 'REVOLVING_BALANCE_LIST',
        entity: 'REVOLVING_BALANCE',
        parentCode: 'REVOLVING_BALANCES',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة الأرصدة المدورة',
        nameEn: 'List carried balances',
    },
    {
        code: 'REVOLVING_BALANCE_VIEW',
        entity: 'REVOLVING_BALANCE',
        parentCode: 'REVOLVING_BALANCES',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل الرصيد المدور',
        nameEn: 'View carried balance details',
    },
    {
        code: 'REVOLVING_BALANCE_DELETE',
        entity: 'REVOLVING_BALANCE',
        parentCode: 'REVOLVING_BALANCES',
        app: 'DASHBOARD',
        nameAr: 'حذف الأرصدة المدورة',
        nameEn: 'Delete carried balances',
    },
    {
        code: 'REVOLVING_BALANCE_EXPORT',
        entity: 'REVOLVING_BALANCE',
        parentCode: 'REVOLVING_BALANCES',
        app: 'DASHBOARD',
        nameAr: 'تصدير الأرصدة المدورة',
        nameEn: 'Export carried balances',
    },
    {
        code: 'PURCHASE_LIST',
        entity: 'PURCHASE',
        parentCode: 'PURCHASES',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المشتريات',
        nameEn: 'List purchases',
    },
    {
        code: 'PURCHASE_VIEW',
        entity: 'PURCHASE',
        parentCode: 'PURCHASES',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل الشراء',
        nameEn: 'View purchase details',
    },
    {
        code: 'PURCHASE_DELETE',
        entity: 'PURCHASE',
        parentCode: 'PURCHASES',
        app: 'DASHBOARD',
        nameAr: 'حذف المشتريات',
        nameEn: 'Delete purchases',
    },
    {
        code: 'PURCHASE_EXPORT',
        entity: 'PURCHASE',
        parentCode: 'PURCHASES',
        app: 'DASHBOARD',
        nameAr: 'تصدير المشتريات',
        nameEn: 'Export purchases',
    },
    {
        code: 'PURCHASE_PRICE_COMPLETE',
        entity: 'PURCHASE',
        parentCode: 'PURCHASES',
        app: 'DASHBOARD',
        nameAr: 'إكمال سعر الشراء',
        nameEn: 'Complete purchase price',
    },
    {
        code: 'SALE_LIST',
        entity: 'SALE',
        parentCode: 'SALES',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المبيعات',
        nameEn: 'List sales',
    },
    {
        code: 'SALE_VIEW',
        entity: 'SALE',
        parentCode: 'SALES',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل البيع',
        nameEn: 'View sale details',
    },
    {
        code: 'SALE_DELETE',
        entity: 'SALE',
        parentCode: 'SALES',
        app: 'DASHBOARD',
        nameAr: 'حذف المبيعات',
        nameEn: 'Delete sales',
    },
    {
        code: 'SALE_EXPORT',
        entity: 'SALE',
        parentCode: 'SALES',
        app: 'DASHBOARD',
        nameAr: 'تصدير المبيعات',
        nameEn: 'Export sales',
    },
    {
        code: 'DEBT_MOVEMENT_LIST',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة حركات الذمم',
        nameEn: 'List debt movements',
    },
    {
        code: 'DEBT_MOVEMENT_VIEW',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل حركة الذمم',
        nameEn: 'View debt movement details',
    },
    {
        code: 'DEBT_MOVEMENT_MANUAL_CREATE',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'إضافة حركة يدوية',
        nameEn: 'Add manual movement',
    },
    {
        code: 'DEBT_MOVEMENT_UPDATE',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'تعديل حركة ذمم',
        nameEn: 'Edit debt movement',
    },
    {
        code: 'DEBT_MOVEMENT_DELETE',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'حذف حركات الذمم',
        nameEn: 'Delete debt movements',
    },
    {
        code: 'DEBT_MOVEMENT_EXPORT',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DEBT_MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'تصدير حركات الذمم',
        nameEn: 'Export debt movements',
    },
    {
        code: 'PAYMENT_LIST',
        entity: 'PAYMENT',
        parentCode: 'PAYMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المدفوعات',
        nameEn: 'List payments',
    },
    {
        code: 'PAYMENT_VIEW',
        entity: 'PAYMENT',
        parentCode: 'PAYMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل الدفعة',
        nameEn: 'View payment details',
    },
    {
        code: 'PAYMENT_DELETE',
        entity: 'PAYMENT',
        parentCode: 'PAYMENTS',
        app: 'DASHBOARD',
        nameAr: 'حذف المدفوعات',
        nameEn: 'Delete payments',
    },
    {
        code: 'PAYMENT_EXPORT',
        entity: 'PAYMENT',
        parentCode: 'PAYMENTS',
        app: 'DASHBOARD',
        nameAr: 'تصدير المدفوعات',
        nameEn: 'Export payments',
    },
    {
        code: 'RECEIPT_LIST',
        entity: 'RECEIPT',
        parentCode: 'RECEIPTS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المقبوضات',
        nameEn: 'List receipts',
    },
    {
        code: 'RECEIPT_VIEW',
        entity: 'RECEIPT',
        parentCode: 'RECEIPTS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل القبض',
        nameEn: 'View receipt details',
    },
    {
        code: 'RECEIPT_DELETE',
        entity: 'RECEIPT',
        parentCode: 'RECEIPTS',
        app: 'DASHBOARD',
        nameAr: 'حذف المقبوضات',
        nameEn: 'Delete receipts',
    },
    {
        code: 'RECEIPT_EXPORT',
        entity: 'RECEIPT',
        parentCode: 'RECEIPTS',
        app: 'DASHBOARD',
        nameAr: 'تصدير المقبوضات',
        nameEn: 'Export receipts',
    },
    {
        code: 'MOVEMENT_HISTORY_VIEW',
        entity: 'MOVEMENT_HISTORY',
        parentCode: 'MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض الحركات السابقة والفلترة',
        nameEn: 'View past movements and filters',
    },
    {
        code: 'MOVEMENT_TOTALS_VIEW',
        entity: 'MOVEMENT_TOTALS',
        parentCode: 'MOVEMENTS',
        app: 'DASHBOARD',
        nameAr: 'عرض المجاميع',
        nameEn: 'View totals',
    },
    {
        code: 'DEBTOR_LIST',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة عملاء الذمم',
        nameEn: 'List debtors',
    },
    {
        code: 'DEBTOR_VIEW',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل المدين',
        nameEn: 'View debtor details',
    },
    {
        code: 'DEBTOR_CREATE',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'إضافة مدين',
        nameEn: 'Add debtor',
    },
    {
        code: 'DEBTOR_UPDATE',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'تعديل المدين والمركبات',
        nameEn: 'Edit debtor and vehicles',
    },
    {
        code: 'DEBTOR_DELETE',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'حذف المدين',
        nameEn: 'Delete debtor',
    },
    {
        code: 'DEBTOR_BALANCE_ADJUST',
        entity: 'DEBTOR',
        parentCode: 'DEBTORS',
        app: 'DASHBOARD',
        nameAr: 'تعديل إجمالي الدين',
        nameEn: 'Adjust total debt',
    },
    {
        code: 'MESSAGE_SEND',
        entity: 'MESSAGE',
        parentCode: 'MESSAGES',
        app: 'DASHBOARD',
        nameAr: 'إرسال رسالة',
        nameEn: 'Send a message',
    },
    {
        code: 'MESSAGE_RETRY',
        entity: 'MESSAGE',
        parentCode: 'MESSAGES',
        app: 'DASHBOARD',
        nameAr: 'إعادة إرسال الرسالة',
        nameEn: 'Resend a message',
    },
    {
        code: 'SALE_DELIVER',
        entity: 'SALE',
        parentCode: 'DELIVERY',
        app: 'DASHBOARD',
        nameAr: 'تأكيد تسليم المبيعات',
        nameEn: 'Confirm sale delivery',
    },
    {
        code: 'DEBT_MOVEMENT_DELIVER',
        entity: 'DEBT_MOVEMENT',
        parentCode: 'DELIVERY',
        app: 'DASHBOARD',
        nameAr: 'تأكيد تسليم بيع الذمم',
        nameEn: 'Confirm debt sale delivery',
    },
    {
        code: 'YEAR_MANAGE',
        entity: 'YEAR',
        parentCode: 'YEARS',
        app: 'DASHBOARD',
        nameAr: 'إدارة السنوات',
        nameEn: 'Manage years',
    },
    {
        code: 'CLOSE_LIST',
        entity: 'CLOSE',
        parentCode: 'CLOSES',
        app: 'DASHBOARD',
        nameAr: 'عرض حركات الإغلاق',
        nameEn: 'List shift closes',
    },
    {
        code: 'INVENTORY_MODE_MANAGE',
        entity: 'INVENTORY_MODE',
        parentCode: 'INVENTORY_COUNT',
        app: 'DASHBOARD',
        nameAr: 'إدارة وضع الجرد',
        nameEn: 'Manage inventory count mode',
    },
    {
        code: 'PENDING_TRANSACTION_LIST',
        entity: 'PENDING_TRANSACTION',
        parentCode: 'INVENTORY_COUNT',
        app: 'DASHBOARD',
        nameAr: 'عرض الحركات المعلقة',
        nameEn: 'List pending transactions',
    },
    {
        code: 'PENDING_TRANSACTION_POST',
        entity: 'PENDING_TRANSACTION',
        parentCode: 'INVENTORY_COUNT',
        app: 'DASHBOARD',
        nameAr: 'ترحيل الحركات المعلقة',
        nameEn: 'Post pending transactions',
    },
    {
        code: 'TRASH_LIST',
        entity: 'TRASH',
        parentCode: 'TRASH',
        app: 'DASHBOARD',
        nameAr: 'عرض سلة المحذوفات',
        nameEn: 'View trash',
    },
    {
        code: 'TRASH_RESTORE',
        entity: 'TRASH',
        parentCode: 'TRASH',
        app: 'DASHBOARD',
        nameAr: 'استعادة من سلة المحذوفات',
        nameEn: 'Restore from trash',
    },
    {
        code: 'TRASH_DELETE',
        entity: 'TRASH',
        parentCode: 'TRASH',
        app: 'DASHBOARD',
        nameAr: 'الحذف نهائيا من سلة المحذوفات',
        nameEn: 'Delete permanently from trash',
    },
    {
        code: 'CATEGORY_LIST',
        entity: 'CATEGORY',
        parentCode: 'CATEGORIES',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة التصنيفات',
        nameEn: 'List categories',
    },
    {
        code: 'CATEGORY_VIEW',
        entity: 'CATEGORY',
        parentCode: 'CATEGORIES',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل التصنيف',
        nameEn: 'View category details',
    },
    {
        code: 'CATEGORY_CREATE',
        entity: 'CATEGORY',
        parentCode: 'CATEGORIES',
        app: 'DASHBOARD',
        nameAr: 'إضافة تصنيف',
        nameEn: 'Add category',
    },
    {
        code: 'CATEGORY_DELETE',
        entity: 'CATEGORY',
        parentCode: 'CATEGORIES',
        app: 'DASHBOARD',
        nameAr: 'حذف التصنيف',
        nameEn: 'Delete category',
    },
    {
        code: 'SETTINGS_VIEW',
        entity: 'SETTINGS',
        parentCode: 'SETTINGS',
        app: 'DASHBOARD',
        nameAr: 'عرض الأسعار والإعدادات',
        nameEn: 'View prices and settings',
    },
    {
        code: 'SETTINGS_UPDATE',
        entity: 'SETTINGS',
        parentCode: 'SETTINGS',
        app: 'DASHBOARD',
        nameAr: 'تعديل الأسعار والإعدادات',
        nameEn: 'Edit prices and settings',
    },
    {
        code: 'USER_LIST',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المستخدمين',
        nameEn: 'List users',
    },
    {
        code: 'USER_VIEW',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل المستخدم',
        nameEn: 'View user details',
    },
    {
        code: 'USER_CREATE',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'إضافة مستخدم',
        nameEn: 'Add user',
    },
    {
        code: 'USER_UPDATE',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'تعديل المستخدم',
        nameEn: 'Edit user',
    },
    {
        code: 'USER_CHANGE_STATUS',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'تغيير حالة المستخدم',
        nameEn: 'Change user status',
    },
    {
        code: 'USER_RESET_PASSWORD',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'إعادة تعيين كلمة المرور',
        nameEn: 'Reset password',
    },
    {
        code: 'USER_DELETE',
        entity: 'USER',
        parentCode: 'USERS',
        app: 'DASHBOARD',
        nameAr: 'حذف المستخدم',
        nameEn: 'Delete user',
    },
    {
        code: 'PDA_PURCHASE_CREATE',
        entity: 'PDA_PURCHASE',
        parentCode: 'PDA',
        app: 'PDA',
        nameAr: 'إدخال الشراء من الـ PDA',
        nameEn: 'Enter purchase on the PDA',
    },
    {
        code: 'PDA_SALE_CREATE',
        entity: 'PDA_SALE',
        parentCode: 'PDA',
        app: 'PDA',
        nameAr: 'إدخال البيع من الـ PDA',
        nameEn: 'Enter sale on the PDA',
    },
    {
        code: 'PDA_DELIVERY_COMPLETE',
        entity: 'PDA_DELIVERY',
        parentCode: 'PDA',
        app: 'PDA',
        nameAr: 'تسليم وصل بيع من الـ PDA',
        nameEn: 'Deliver a sale on the PDA',
    },
    {
        code: 'PDA_RECEIPT_REPRINT',
        entity: 'PDA_RECEIPT',
        parentCode: 'PDA',
        app: 'PDA',
        nameAr: 'إعادة طباعة أو إرسال وصل الشراء من الـ PDA',
        nameEn: 'Reprint or resend a purchase receipt on the PDA',
    },
    {
        code: 'PDA_CUSTOMER_LIST',
        entity: 'PDA_CUSTOMER',
        parentCode: 'PDA_CUSTOMERS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة عملاء المشتريات',
        nameEn: 'List purchase customers',
    },
    {
        code: 'PDA_CUSTOMER_VIEW',
        entity: 'PDA_CUSTOMER',
        parentCode: 'PDA_CUSTOMERS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل عميل المشتريات',
        nameEn: 'View purchase customer details',
    },
    {
        code: 'PDA_CUSTOMER_UPDATE',
        entity: 'PDA_CUSTOMER',
        parentCode: 'PDA_CUSTOMERS',
        app: 'DASHBOARD',
        nameAr: 'تعديل عميل المشتريات',
        nameEn: 'Edit purchase customer',
    },
    {
        code: 'NATIONALITY_LIST',
        entity: 'NATIONALITY',
        parentCode: 'NATIONALITIES',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة الجنسيات',
        nameEn: 'List nationalities',
    },
    {
        code: 'NATIONALITY_CREATE',
        entity: 'NATIONALITY',
        parentCode: 'NATIONALITIES',
        app: 'DASHBOARD',
        nameAr: 'إضافة جنسية',
        nameEn: 'Add nationality',
    },
    {
        code: 'NATIONALITY_UPDATE',
        entity: 'NATIONALITY',
        parentCode: 'NATIONALITIES',
        app: 'DASHBOARD',
        nameAr: 'تعديل الجنسية',
        nameEn: 'Edit nationality',
    },
    {
        code: 'NATIONALITY_DELETE',
        entity: 'NATIONALITY',
        parentCode: 'NATIONALITIES',
        app: 'DASHBOARD',
        nameAr: 'حذف الجنسية',
        nameEn: 'Delete nationality',
    },
    {
        code: 'GROUP_LIST',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'عرض قائمة المجموعات',
        nameEn: 'List groups',
    },
    {
        code: 'GROUP_VIEW',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'عرض تفاصيل المجموعة',
        nameEn: 'View group details',
    },
    {
        code: 'GROUP_CREATE',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'إضافة مجموعة',
        nameEn: 'Add group',
    },
    {
        code: 'GROUP_UPDATE',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'تعديل المجموعة',
        nameEn: 'Edit group',
    },
    {
        code: 'GROUP_CHANGE_STATUS',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'تغيير حالة المجموعة',
        nameEn: 'Change group status',
    },
    {
        code: 'GROUP_DELETE',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'حذف المجموعة',
        nameEn: 'Delete group',
    },
    {
        code: 'GROUP_SERVICE_MANAGE',
        entity: 'GROUP',
        parentCode: 'GROUPS',
        app: 'DASHBOARD',
        nameAr: 'إدارة خدمات المجموعة',
        nameEn: 'Manage group services',
    },
];
const BY_CODE = new Map(SERVICE_CATALOG.map((s) => [s.code, s]));
export function isServiceCode(code) {
    return BY_CODE.has(code);
}
export function serviceDefinition(code) {
    return BY_CODE.get(code);
}
/** Every service code: the built-in مدير النظام group holds all of them. */
export const ALL_SERVICE_CODES = SERVICE_CATALOG.map((s) => s.code);
/**
 * The dependency rule (00-overview.md section 4): any service on an entity
 * also grants that entity's `_LIST` and `_VIEW`, where those exist. Unknown
 * codes are rejected so a group can never store a service the catalog lacks.
 */
export function computeDependencyClosure(selected) {
    const result = new Set();
    for (const code of selected) {
        const definition = BY_CODE.get(code);
        if (!definition)
            throw new RangeError(`Unknown service code: ${code}`);
        result.add(code);
        const list = `${definition.entity}_LIST`;
        const view = `${definition.entity}_VIEW`;
        if (code === list)
            continue;
        if (BY_CODE.has(list))
            result.add(list);
        if (code === view)
            continue;
        if (BY_CODE.has(view))
            result.add(view);
    }
    return [...result].toSorted();
}
/**
 * Removing `_LIST` or `_VIEW` removes every service that depends on it
 * (the inverse of the closure, used by the group editor).
 */
export function removeWithDependants(selected, removed) {
    const definition = BY_CODE.get(removed);
    const isBase = definition !== undefined &&
        (removed === `${definition.entity}_LIST` || removed === `${definition.entity}_VIEW`);
    return selected.filter((code) => {
        if (code === removed)
            return false;
        if (!isBase || !definition)
            return true;
        const other = BY_CODE.get(code);
        if (!other || other.entity !== definition.entity)
            return true;
        // Removing _LIST removes the whole entity; removing _VIEW keeps _LIST only.
        return removed === `${definition.entity}_VIEW` && code === `${definition.entity}_LIST`;
    });
}
/** Which Fuel applications a set of services opens (fuel.md section 6). */
export function appsForServices(codes) {
    const apps = new Set();
    for (const code of codes) {
        const app = BY_CODE.get(code)?.app;
        if (app === 'DASHBOARD' || app === 'OPERATOR' || app === 'PDA')
            apps.add(app);
    }
    return FUEL_APPS.filter((app) => apps.has(app));
}
/** The product access service each product requires on every transaction endpoint. */
export const PRODUCT_ACCESS_SERVICE = {
    DIESEL: 'DIESEL_ACCESS',
    PETROL: 'PETROL_ACCESS',
};
//# sourceMappingURL=services.js.map